/**
 * Barcode Scanner Logic
 * Handles keyboard events from barcode scanners and ID card lookups
 */

// Some scanners (seen on Bluetooth pairings) emit a stray modifier keydown in the middle of an
// ID, which lands in the buffer as its key name ("w1283Shift80"). IDs are plain characters, so
// these names are never part of a real ID.
const MODIFIER_KEY_NAMES = /Shift|Control|Alt|Meta|CapsLock|Dead|Unidentified/g;

// A swallowed scan eats keystrokes until its Enter. If the Enter never comes (a "!" typed by
// hand), stop swallowing after this long so typing isn't eaten.
const SCAN_SWALLOW_TIMEOUT_MS = 1000;

// The wrestler search box is the focused input on the home screen, so a scan's keystrokes would
// otherwise type the ID into it and fire a name search. Inputs opt in with data-kiosk-scan-target;
// any other focused field (e.g. a check-in question) keeps its keystrokes.
function focusIsScanTarget() {
  const el = document.activeElement;
  if (!el || el === document.body) return true;
  const isField = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  return !isField || el.dataset.kioskScanTarget !== undefined;
}

/**
 * options.isScanBlocked() - true while a scan must not land (e.g. the weight modal is open with
 *   its input focused). A blocked scan's keystrokes are swallowed (capture phase) rather than
 *   reaching the focused input, its Enter can't submit the focused form, and no lookup runs.
 * options.onBlockedScan() - called once a blocked scan finishes, to tell the user why nothing
 *   happened.
 */
export function useBarcodeScanner(store, apiService, notificationManager, onWrestlerFound, options = {}) {
  const isScanBlocked = options.isScanBlocked || (() => false);
  const onBlockedScan = options.onBlockedScan || (() => {});
  let scanBuffer = '';
  let lastKey = '';
  // While a scan is being swallowed: { startedAt, blocked }. Unblocked swallowed scans still look
  // up the ID; they just don't echo into the search box.
  let swallowing = null;

  // "!" (or Shift then "1") can only start a scan; nobody types it.
  function isUnambiguousScanPrefix(e) {
    return e.key === '!' || (lastKey === 'Shift' && e.key === '1');
  }

  // Shift then "W" is also someone typing a capitalized word ("William" in search, "Will" in a
  // check-in question answer), and there's no telling the two apart when the prefix arrives. It
  // is only treated as a scan where a capital W can't be typed: a number-only input (the weight
  // and admin PIN fields are type="tel"). Elsewhere a ShiftW scan while blocked still goes
  // through the normal lookup, where the caller's onWrestlerFound guard refuses it.
  function isNumberOnlyFocus() {
    const el = document.activeElement;
    return !!el && el.tagName === 'INPUT' && (el.type === 'tel' || el.type === 'number');
  }

  function isShiftWScanPrefix(e) {
    return lastKey === 'Shift' && e.key === 'W';
  }

  function startSwallowing(e) {
    if (isScanBlocked()) {
      if (isUnambiguousScanPrefix(e) || (isShiftWScanPrefix(e) && isNumberOnlyFocus())) {
        return { startedAt: Date.now(), blocked: true };
      }
      return null;
    }
    if (isUnambiguousScanPrefix(e) && focusIsScanTarget()) return { startedAt: Date.now(), blocked: false };
    return null;
  }

  function keydown(e) {
    if (store.state.debug) {
      console.log('key up fired');
      console.log(e);
      console.log(e.key);
    }

    if (swallowing && Date.now() - swallowing.startedAt > SCAN_SWALLOW_TIMEOUT_MS) swallowing = null;
    if (!swallowing) swallowing = startSwallowing(e);

    if (swallowing) {
      e.preventDefault();
      e.stopPropagation();
      if (swallowing.blocked) {
        if (e.keyCode === 13) {
          swallowing = null;
          scanBuffer = '';
          lastKey = '';
          onBlockedScan();
        }
        return;
      }
      if (e.keyCode === 13) swallowing = null;
    }

    scanBuffer += e.key;
    lastKey = e.key;

    if (e.keyCode === 13) {
      checkInBarcodeId(scanBuffer);
      scanBuffer = '';
      lastKey = '';
    }
  }

  function checkInBarcodeId(scannedValue) {
    const regex = /!(.*)Enter/;
    const exclaimMatch = scannedValue.match(regex);
    const altRegex = /Shift1(.*)Enter/;
    const altMatch = scannedValue.match(altRegex);
    const alt2Regex = /ShiftW(.*)Enter/;
    const alt2Match = scannedValue.match(alt2Regex);

    if (exclaimMatch || altMatch || alt2Match) {
      let scannedMatch = 'unknown';
      if (exclaimMatch) {
        scannedMatch = exclaimMatch[1];
      } else if (altMatch) {
        scannedMatch = altMatch[1];
      } else if (alt2Match) {
        scannedMatch = alt2Match[1];
      }

      scannedMatch = scannedMatch.replace(MODIFIER_KEY_NAMES, '');

      if (store.state.debug) console.log(`Fire check in for ${scannedMatch}`);

      if (store.state.lookingUpId) {
        notificationManager.emit('show-toast', 'Please wait, still looking up previously scanned id', false);
        return;
      }

      store.state.lookingUpId = true;

      const url = `${apiService.athleteIdsUrl()}?scanned=${scannedMatch}`;

      axios.get(url)
        .then((response) => {
          if (response.data.athlete_identifiers.length > 0) {
            const athleteId = response.data.athlete_identifiers[0];
            // Call the callback with the found wrestler profile
            if (onWrestlerFound) {
              onWrestlerFound(athleteId.profile);
            }
          } else {
            notificationManager.emit('show-toast', 'Unable to find id card - please try again', false);
          }
          store.state.lookingUpId = false;
        })
        .catch((error) => {
          store.state.lookingUpId = false;
          notificationManager.emit('show-toast', error.toString(), false);
        });
    } else if (store.state.debug) console.log(`skipping check in for ${scannedValue}`);
  }

  function setupScanner() {
    window.addEventListener('keydown', keydown, true);
  }

  function teardownScanner() {
    window.removeEventListener('keydown', keydown, true);
  }

  return {
    setupScanner,
    teardownScanner,
    checkInBarcodeId,
  };
}
